import {
  WHEEL_STAGE_MODEL_VERSION,
  allGainsForResult,
  backpackFragmentCount,
  calculateRawAttributes,
  itemFragmentCount,
  resourceGroupForCharacter,
  userPlanFromWire,
  type OptimizationStudy,
  type SolverConfiguration,
  type SolverHistoryEntry,
  type SolverResult,
  type Workbench,
} from "@minglun/domain";
import { parseMigratableSolverRecords } from "@minglun/data/solver-record-file";
import { calculateObjectiveScore } from "@minglun/solver/verify";

export const TRANSFER_FORMAT = "minglun-study-transfer";
export const TRANSFER_VERSION = 1;
export const RESULT_TRANSFER_FORMAT = "minglun-result-transfer";

export interface TransferReferences {
  characters: Array<{ id: string; gamePartnerId: number; name: string }>;
  metrics: Array<{ id: string; name: string }>;
  attributes: Array<{ id: string; name: string }>;
  wheels: Array<{ id: string; gameEntryId: number; name: string }>;
}

interface StudyTransferDocument {
  format: typeof TRANSFER_FORMAT;
  version: typeof TRANSFER_VERSION;
  exportedAt: string;
  catalogVersion: string;
  catalogFingerprint: string;
  references: TransferReferences;
  studies: OptimizationStudy[];
}

interface ResultTransferDocument {
  format: typeof RESULT_TRANSFER_FORMAT;
  version: typeof TRANSFER_VERSION;
  exportedAt: string;
  catalogVersion: string;
  catalogFingerprint: string;
  references: TransferReferences;
  records: SolverHistoryEntry[];
}

export interface TransferPreview<T> {
  items: T[];
  warnings: string[];
  defaultsAdded: number;
  remapped: number;
}

export function serializeStudies(studies: OptimizationStudy[], workbench: Workbench): string {
  const document: StudyTransferDocument = {
    format: TRANSFER_FORMAT,
    version: TRANSFER_VERSION,
    exportedAt: new Date().toISOString(),
    catalogVersion: workbench.version,
    catalogFingerprint: catalogFingerprint(workbench),
    references: createTransferReferences(workbench),
    studies: structuredClone(studies),
  };
  return `${JSON.stringify(document, null, 2)}\n`;
}

export function serializeResults(entries: SolverHistoryEntry[], workbench: Workbench): string {
  const document: ResultTransferDocument = {
    format: RESULT_TRANSFER_FORMAT,
    version: TRANSFER_VERSION,
    exportedAt: new Date().toISOString(),
    catalogVersion: workbench.version,
    catalogFingerprint: catalogFingerprint(workbench),
    references: createTransferReferences(workbench),
    records: structuredClone(entries),
  };
  return `${JSON.stringify(document, null, 2)}\n`;
}

export function parseResults(text: string, workbench: Workbench, targetStudies: OptimizationStudy[] = []): TransferPreview<SolverHistoryEntry> {
  let raw: unknown;
  try { raw = JSON.parse(text); }
  catch { throw new Error("结果文件不是有效的命轮工坊数据文件。"); }
  const record = asRecord(raw);
  let entries: SolverHistoryEntry[];
  let references = emptyReferences();
  if (record.format === RESULT_TRANSFER_FORMAT || record.format === "minglun-web-results" || record.format === "minglun-web-result") {
    const source = Array.isArray(record.records) ? record.records : record.record ? [record.record] : [];
    entries = source as SolverHistoryEntry[];
    references = normalizeReferences(record.references);
  } else if (record.format === "minglun-result-history" && Array.isArray(record.records)) {
    entries = parseMigratableSolverRecords(text, workbench).records;
  } else if (Array.isArray(raw)) entries = raw as SolverHistoryEntry[];
  else throw new Error("文件中没有可迁移的求解结果。");
  if (!entries.length) throw new Error("文件中没有求解结果。");
  const sourceFingerprint = typeof record.catalogFingerprint === "string" ? record.catalogFingerprint : undefined;
  const preview = migrateResults(entries, workbench, references, targetStudies, sourceFingerprint === catalogFingerprint(workbench));
  const sourceVersion = typeof record.catalogVersion === "string" ? record.catalogVersion : undefined;
  if (sourceVersion && sourceVersion !== workbench.version) {
    preview.warnings.unshift(`结果来自资料库 ${sourceVersion}，将按当前资料库 ${workbench.version} 重建引用、属性与评分。`);
  }
  return preview;
}

export function parseStudies(text: string, workbench: Workbench): TransferPreview<OptimizationStudy> {
  let raw: unknown;
  try { raw = JSON.parse(text); }
  catch { throw new Error("方案文件不是有效的命轮工坊数据文件。"); }
  const record = asRecord(raw);
  let studies: OptimizationStudy[] = [];
  let references = emptyReferences();
  if (record.format === TRANSFER_FORMAT) {
    if (record.version !== TRANSFER_VERSION || !Array.isArray(record.studies)) throw new Error("方案文件版本无法识别。");
    studies = record.studies as OptimizationStudy[];
    references = normalizeReferences(record.references);
  } else {
    const payload = record.format === "minglun-user-plan-cache" ? asRecord(record.plan) : record;
    try {
      const plan = userPlanFromWire(payload);
      studies = plan.studies?.length ? plan.studies : [{
        id: plan.activeStudyId ?? "study.imported",
        name: plan.name || "导入的方案",
        createdAt: plan.updatedAt || new Date().toISOString(),
        updatedAt: plan.updatedAt || new Date().toISOString(),
        baseAttributes: plan.baseAttributes,
        objectives: plan.objectives,
        inventory: plan.inventory,
        solver: plan.solver,
        ...(plan.lastResult ? { lastResult: plan.lastResult } : {}),
      }];
    } catch {
      if (isStudy(record)) studies = [record as unknown as OptimizationStudy];
      else throw new Error("文件中没有可迁移的方案。");
    }
  }
  if (!studies.length) throw new Error("文件中没有方案。");
  const sourceFingerprint = typeof record.catalogFingerprint === "string" ? record.catalogFingerprint : undefined;
  const preview = migrateStudies(studies, workbench, references, sourceFingerprint === catalogFingerprint(workbench));
  const sourceVersion = typeof record.catalogVersion === "string" ? record.catalogVersion : undefined;
  if (sourceVersion && sourceVersion !== workbench.version) {
    preview.warnings.unshift(`方案来自资料库 ${sourceVersion}，将按当前资料库 ${workbench.version} 补齐并迁移。`);
  }
  return preview;
}

export function migrateStudies(
  studies: OptimizationStudy[],
  workbench: Workbench,
  references = emptyReferences(),
  preserveDerivedData = false,
): TransferPreview<OptimizationStudy> {
  const context = migrationContext(workbench, references);
  const warnings: string[] = [];
  let defaultsAdded = 0;
  let remapped = 0;
  const items = studies.map((study, index) => {
    const migrated = migrateConfiguration(study, workbench, context, warnings);
    defaultsAdded += migrated.defaultsAdded;
    remapped += migrated.remapped;
    return {
      id: `study.${crypto.randomUUID()}`,
      name: String(study.name || `导入的方案 ${index + 1}`),
      ...(study.description ? { description: String(study.description) } : {}),
      createdAt: validDate(study.createdAt),
      updatedAt: new Date().toISOString(),
      baseAttributes: migrated.baseAttributes,
      objectives: migrated.objectives,
      inventory: migrated.inventory,
      solver: migrated.solver,
      ...(study.lastResult ? { lastResult: refreshResult(migrateResult(study.lastResult, workbench, context, warnings, preserveDerivedData), migrated, workbench) } : {}),
    } satisfies OptimizationStudy;
  });
  if (defaultsAdded) warnings.push(`当前资料库新增的目标、参考、命轮资源或求解参数共 ${defaultsAdded} 项，确认后将使用当前默认值补齐。`);
  return { items, warnings: unique(warnings), defaultsAdded, remapped };
}

export function migrateResults(
  entries: SolverHistoryEntry[],
  workbench: Workbench,
  references = emptyReferences(),
  targetStudies: OptimizationStudy[] = [],
  preserveDerivedData = false,
): TransferPreview<SolverHistoryEntry> {
  const context = migrationContext(workbench, references);
  const warnings: string[] = [];
  let defaultsAdded = 0;
  let remapped = 0;
  const migratedItems = entries.map((entry) => {
    const migrated = migrateConfiguration({
      baseAttributes: entry.baseAttributes,
      objectives: entry.objectives,
      inventory: entry.inventory,
      solver: entry.configuration,
    }, workbench, context, warnings);
    defaultsAdded += migrated.defaultsAdded;
    remapped += migrated.remapped;
    const result = refreshResult(migrateResult(entry.result, workbench, context, warnings, preserveDerivedData), migrated, workbench);
    const comparisonExecutions = (entry.comparisonExecutions ?? []).flatMap((progress) => {
      const appliedFateWheelIds = progress.appliedFateWheelIds.flatMap((legacyId) => {
        const fateWheelId = context.wheel(legacyId);
        if (fateWheelId) return [fateWheelId];
        warnings.push(`结果“${entry.title || "导入的求解结果"}”的执行进度引用了已删除的关系命轮“${context.wheelName(legacyId)}”，确认后将移除该项。`);
        return [];
      });
      return appliedFateWheelIds.length ? [{ ...progress, appliedFateWheelIds }] : [];
    });
    const linkedStudy = matchStudy(entry, targetStudies);
    if (targetStudies.length && !linkedStudy) {
      warnings.push(`结果“${entry.title || "导入的求解结果"}”原属方案“${entry.studyName ?? entry.studyId ?? "未知方案"}”，当前方案中无法匹配，确认后将归入“${targetStudies[0]!.name}”。`);
    }
    const study = linkedStudy ?? targetStudies[0];
    return {
      ...structuredClone(entry),
      id: `record.${crypto.randomUUID()}`,
      title: entry.title || "导入的求解结果",
      workbenchVersion: workbench.version,
      ...(study ? { studyId: study.id, studyName: study.name } : {}),
      ...(comparisonExecutions.length ? { comparisonExecutions } : { comparisonExecutions: [] }),
      configuration: migrated.solver,
      objectives: migrated.objectives,
      baseAttributes: migrated.baseAttributes,
      inventory: migrated.inventory,
      result,
    };
  });
  const baselineByCompletedAt = new Map(migratedItems.map((entry) => [entry.result.completedAt, entry.result]));
  const items = migratedItems.map((entry) => {
    const comparisonExecutions = (entry.comparisonExecutions ?? []).flatMap((progress) => {
      const baseline = baselineByCompletedAt.get(progress.baselineCompletedAt);
      if (!baseline) return [];
      const left = new Map(baseline.fateWheels.filter(isActiveResultWheel).map((item) => [item.fateWheelId, item]));
      const right = new Map(entry.result.fateWheels.filter(isActiveResultWheel).map((item) => [item.fateWheelId, item]));
      const validDifferences = new Set([...new Set([...left.keys(), ...right.keys()])].filter((id) => {
        const before = left.get(id);
        const after = right.get(id);
        return !before || !after || before.rank !== after.rank || before.star !== after.star;
      }));
      const appliedFateWheelIds = progress.appliedFateWheelIds.filter((id) => validDifferences.has(id));
      return appliedFateWheelIds.length ? [{ ...progress, appliedFateWheelIds }] : [];
    });
    return { ...entry, comparisonExecutions };
  });
  if (defaultsAdded) warnings.push(`当前资料库新增的目标、参考、命轮资源或求解参数共 ${defaultsAdded} 项，确认后将使用当前默认值补齐。`);
  return { items, warnings: unique(warnings), defaultsAdded, remapped };
}

function refreshResult(
  result: SolverResult,
  configuration: ReturnType<typeof migrateConfiguration>,
  workbench: Workbench,
): SolverResult {
  const userPlan = {
    schemaVersion: "4.1",
    id: "plan.transfer-preview",
    name: "迁移预检",
    workbenchVersion: workbench.version,
    updatedAt: new Date().toISOString(),
    baseAttributes: configuration.baseAttributes,
    baseAttributePresets: [],
    objectives: configuration.objectives,
    objectivePresets: [],
    inventory: configuration.inventory,
    solver: configuration.solver,
    solverHistory: [],
  };
  const fateWheels = result.fateWheels;
  return {
    ...result,
    objectiveScore: calculateObjectiveScore({ workbench, userPlan }, { fateWheels }),
    finalAttributes: calculateRawAttributes(workbench, allGainsForResult(workbench, { fateWheels })),
  };
}

function migrateConfiguration(
  source: Pick<OptimizationStudy, "baseAttributes" | "objectives" | "inventory" | "solver">,
  workbench: Workbench,
  context: ReturnType<typeof migrationContext>,
  warnings: string[],
): { baseAttributes: OptimizationStudy["baseAttributes"]; objectives: OptimizationStudy["objectives"]; inventory: OptimizationStudy["inventory"]; solver: SolverConfiguration; defaultsAdded: number; remapped: number } {
  let remapped = 0;
  const objectiveValues = new Map<string, OptimizationStudy["objectives"][number]>();
  for (const item of source.objectives ?? []) {
    const id = context.metric(item.metricId);
    if (!id) { warnings.push(`目标“${context.metricName(item.metricId)}”已不存在，确认后将删除。`); continue; }
    if (id !== item.metricId) remapped += 1;
    objectiveValues.set(id, { metricId: id, weight: clampInteger(item.weight, 0, 100), isEnabled: item.weight > 0 });
  }
  const objectives = workbench.optimizationMetrics.map((metric) => objectiveValues.get(metric.id) ?? ({ metricId: metric.id, weight: metric.defaultWeight, isEnabled: metric.defaultWeight > 0 }));
  const baseValues = new Map<string, number>();
  for (const item of source.baseAttributes ?? []) {
    const id = context.metric(item.metricId);
    if (!id) { warnings.push(`参考属性“${context.metricName(item.metricId)}”已不存在，确认后将删除。`); continue; }
    if (id !== item.metricId) remapped += 1;
    baseValues.set(id, finite(item.value));
  }
  const defaultBase = new Map(workbench.defaults.userBaseAttributeTemplate.values.map((item) => [item.metricId, item.value]));
  const baseAttributes = workbench.optimizationMetrics
    .filter((metric) => metric.requiresBaseValue)
    .map((metric) => ({ metricId: metric.id, value: baseValues.get(metric.id) ?? defaultBase.get(metric.id) ?? 0 }));
  const characterById = new Map(workbench.characters.map((character) => [character.id, character]));
  const legacyIgnoredGroups = source.solver?.ignoredFragmentGroups ?? [];
  const inventoryValues = new Map<string, { wheelCount: number; backpackFragmentCount: number; itemFragmentCount: number; ignoreFragments: boolean }>();
  for (const item of source.inventory ?? []) {
    const id = context.character(item.characterId);
    if (!id) { warnings.push(`命轮“${context.characterName(item.characterId)}”已不存在，确认后将删除其资源。`); continue; }
    if (id !== item.characterId) remapped += 1;
    const character = characterById.get(id);
    inventoryValues.set(id, {
      wheelCount: nonnegative(item.wheelCount),
      backpackFragmentCount: backpackFragmentCount(item),
      itemFragmentCount: itemFragmentCount(item),
      ignoreFragments: item.ignoreFragments === true || Boolean(character && legacyIgnoredGroups.includes(resourceGroupForCharacter(character))),
    });
  }
  const inventory = workbench.characters.map((character) => ({ characterId: character.id, ...(inventoryValues.get(character.id) ?? { wheelCount: 0, backpackFragmentCount: 0, itemFragmentCount: 0, ignoreFragments: false }) }));
  const algorithm = workbench.defaults.solverAlgorithms.find((item) => item.id === source.solver?.algorithmId)
    ?? workbench.defaults.solverAlgorithms.find((item) => item.isEnabled)
    ?? workbench.defaults.solverAlgorithms[0];
  if (!algorithm) throw new Error("当前资料库没有可用求解器。");
  if (algorithm.id !== source.solver?.algorithmId) warnings.push("原方案使用的求解器已不可用，确认后将改用当前默认求解器。");
  const sourceParameters = new Map((source.solver?.parameters ?? []).map((item) => [item.key, item.value]));
  const parameters = algorithm.parameters.filter((parameter) => parameter.key !== "time_limit_seconds" && parameter.key !== "threads").map((parameter) => ({ key: parameter.key, value: sourceParameters.get(parameter.key) ?? parameter.defaultValue }));
  const solver: SolverConfiguration = {
    algorithmId: algorithm.id,
    includeDestinyWheel: source.solver?.includeDestinyWheel !== false,
    ignoredFragmentGroups: [],
    ssrWheelChoices: nonnegative(source.solver?.ssrWheelChoices),
    ssrChoiceBalanceEnabled: source.solver?.ssrChoiceBalanceEnabled === true,
    preferFewerWheels: source.solver?.preferFewerWheels !== false,
    fragmentAnalysisEnabled: source.solver?.fragmentAnalysisEnabled === true,
    parameters,
  };
  const defaultsAdded = objectives.filter((item) => !objectiveValues.has(item.metricId)).length
    + baseAttributes.filter((item) => !baseValues.has(item.metricId)).length
    + inventory.filter((item) => !inventoryValues.has(item.characterId)).length
    + parameters.filter((item) => !sourceParameters.has(item.key)).length;
  return { baseAttributes, objectives, inventory, solver, defaultsAdded, remapped };
}

function migrateResult(result: SolverResult, workbench: Workbench, context: ReturnType<typeof migrationContext>, warnings: string[], preserveAnalysis = false): SolverResult {
  const wheelById = new Map(workbench.fateWheels.map((item) => [item.id, item]));
  const attributeIds = new Set(workbench.sourceAttributes.map((item) => item.id));
  const characterIds = new Set(workbench.characters.map((item) => item.id));
  const mapWheel = (id: string): string | undefined => context.wheel(id);
  const mapAttribute = (id: string): string | undefined => context.attribute(id);
  const migratedWheels = (result.fateWheels ?? []).flatMap((item) => {
    const id = mapWheel(item.fateWheelId);
    const wheel = id ? wheelById.get(id) : undefined;
    if (!id || !wheel) { warnings.push(`结果中的关系命轮“${context.wheelName(item.fateWheelId)}”已不存在，确认后将删除。`); return []; }
    const rank = clampInteger(item.rank, 0, workbench.globalSettings.maxRank);
    const star = clampInteger(item.star, 0, wheel.maxStar);
    if (rank !== item.rank || star !== item.star) warnings.push(`关系命轮“${wheel.name}”的阶星上限已变化，确认后将调整为 ${rank} 阶 ${star} 星。`);
    return [{ ...item, fateWheelId: id, rank, star }];
  });
  const migratedById = new Map(migratedWheels.filter(isActiveResultWheel).map((item) => [item.fateWheelId, item]));
  const addedWheelCount = workbench.fateWheels.filter((wheel) => !migratedById.has(wheel.id)).length;
  if (addedWheelCount) warnings.push(`当前资料库新增 ${addedWheelCount} 个关系命轮；旧结果保持原样，新命轮默认未选择。`);
  const fateWheels = [...migratedById.values()];
  const finalAttributes = (result.finalAttributes ?? []).flatMap((item) => {
    const id = mapAttribute(item.sourceAttributeId);
    if (!id || !attributeIds.has(id)) { warnings.push(`结果属性“${context.attributeName(item.sourceAttributeId)}”已不存在，确认后将删除。`); return []; }
    return [{ ...item, sourceAttributeId: id }];
  });
  const ssrChoiceSuggestions = (result.ssrChoiceSuggestions ?? []).flatMap((item) => {
    const id = context.character(item.characterId);
    if (id && characterIds.has(id)) return [{ ...item, characterId: id }];
    warnings.push(`SSR 自选建议中的命轮“${context.characterName(item.characterId)}”已不存在，确认后将删除。`);
    return [];
  });
  const analysis = preserveAnalysis ? result.fragmentAnalysis : undefined;
  if (result.fragmentAnalysis && !preserveAnalysis) warnings.push("碎片分析依赖原资料库的求解环境，确认迁移后将移除；重新计算即可生成新版分析。");
  const fragmentAnalysis = analysis ? {
    ...analysis,
    shortages: analysis.shortages.flatMap((item) => {
      const characterId = context.character(item.characterId);
      if (!characterId) return [];
      return [{ ...item, characterId, affectedFateWheelIds: item.affectedFateWheelIds.map(mapWheel).filter((id): id is string => Boolean(id)) }];
    }),
    wheelChanges: analysis.wheelChanges.flatMap((item) => {
      const fateWheelId = mapWheel(item.fateWheelId);
      if (!fateWheelId) return [];
      return [{ ...item, fateWheelId, gains: item.gains.flatMap((gain) => {
        const sourceAttributeId = mapAttribute(gain.sourceAttributeId);
        return sourceAttributeId ? [{ ...gain, sourceAttributeId }] : [];
      }), fragmentChanges: item.fragmentChanges.flatMap((change) => {
        const characterId = context.character(change.characterId);
        return characterId ? [{ ...change, characterId }] : [];
      }) }];
    }),
  } : undefined;
  return {
    ...structuredClone(result),
    fateWheels,
    finalAttributes,
    ...(ssrChoiceSuggestions.length ? { ssrChoiceSuggestions } : { ssrChoiceSuggestions: [] }),
    ...(fragmentAnalysis ? { fragmentAnalysis } : {}),
    stageModelVersion: WHEEL_STAGE_MODEL_VERSION,
    message: result.message ? `${result.message}；已迁移到资料库 ${workbench.version}` : `已迁移到资料库 ${workbench.version}`,
  };
}

export function createTransferReferences(workbench: Workbench): TransferReferences {
  return {
    characters: workbench.characters.map(({ id, gamePartnerId, name }) => ({ id, gamePartnerId, name })),
    metrics: workbench.optimizationMetrics.map(({ id, name }) => ({ id, name })),
    attributes: workbench.sourceAttributes.map(({ id, name }) => ({ id, name })),
    wheels: workbench.fateWheels.map(({ id, gameEntryId, name }) => ({ id, gameEntryId, name })),
  };
}

/** A compact identity only; exported business files never embed the catalog. */
export function catalogFingerprint(workbench: Workbench): string {
  const source = JSON.stringify([
    workbench.characters,
    workbench.optimizationMetrics,
    workbench.sourceAttributes,
    workbench.wheelCategories,
    workbench.wheelTags,
    workbench.fateWheels,
    workbench.globalSettings.maxRank,
  ]);
  let hash = 2166136261;
  for (let index = 0; index < source.length; index += 1) {
    hash ^= source.charCodeAt(index);
    hash = Math.imul(hash, 16777619) >>> 0;
  }
  return `catalog.${hash.toString(16).padStart(8, "0")}`;
}

function migrationContext(workbench: Workbench, refs: TransferReferences) {
  const characterById = new Map(workbench.characters.map((item) => [item.id, item]));
  const characterByGame = new Map(workbench.characters.map((item) => [item.gamePartnerId, item.id]));
  const characterByName = uniqueNameMap(workbench.characters);
  const characterRef = new Map(refs.characters.map((item) => [item.id, item]));
  const metricById = new Map(workbench.optimizationMetrics.map((item) => [item.id, item]));
  const metricByName = uniqueNameMap(workbench.optimizationMetrics);
  const metricRef = new Map(refs.metrics.map((item) => [item.id, item]));
  const attributeById = new Map(workbench.sourceAttributes.map((item) => [item.id, item]));
  const attributeByName = uniqueNameMap(workbench.sourceAttributes);
  const attributeRef = new Map(refs.attributes.map((item) => [item.id, item]));
  const wheelById = new Map(workbench.fateWheels.map((item) => [item.id, item]));
  const wheelByGame = new Map(workbench.fateWheels.map((item) => [item.gameEntryId, item.id]));
  const wheelByName = uniqueNameMap(workbench.fateWheels);
  const wheelRef = new Map(refs.wheels.map((item) => [item.id, item]));
  return {
    character: (id: string) => characterById.has(id) ? id : characterByGame.get(characterRef.get(id)?.gamePartnerId ?? NaN) ?? characterByName.get(normalize(characterRef.get(id)?.name ?? "")),
    characterName: (id: string) => characterRef.get(id)?.name ?? characterById.get(id)?.name ?? id,
    metric: (id: string) => metricById.has(id) ? id : metricByName.get(normalize(metricRef.get(id)?.name ?? "")),
    metricName: (id: string) => metricRef.get(id)?.name ?? metricById.get(id)?.name ?? id,
    attribute: (id: string) => attributeById.has(id) ? id : attributeByName.get(normalize(attributeRef.get(id)?.name ?? "")),
    attributeName: (id: string) => attributeRef.get(id)?.name ?? attributeById.get(id)?.name ?? id,
    wheel: (id: string) => wheelById.has(id) ? id : wheelByGame.get(wheelRef.get(id)?.gameEntryId ?? NaN) ?? wheelByName.get(normalize(wheelRef.get(id)?.name ?? "")),
    wheelName: (id: string) => wheelRef.get(id)?.name ?? wheelById.get(id)?.name ?? id,
  };
}

function normalizeReferences(value: unknown): TransferReferences {
  const record = asRecord(value);
  return {
    characters: Array.isArray(record.characters) ? record.characters as TransferReferences["characters"] : [],
    metrics: Array.isArray(record.metrics) ? record.metrics as TransferReferences["metrics"] : [],
    attributes: Array.isArray(record.attributes) ? record.attributes as TransferReferences["attributes"] : [],
    wheels: Array.isArray(record.wheels) ? record.wheels as TransferReferences["wheels"] : [],
  };
}
function emptyReferences(): TransferReferences { return { characters: [], metrics: [], attributes: [], wheels: [] }; }
function uniqueNameMap(items: Array<{ id: string; name: string }>): Map<string, string> {
  const grouped = new Map<string, string[]>();
  for (const item of items) grouped.set(normalize(item.name), [...(grouped.get(normalize(item.name)) ?? []), item.id]);
  return new Map([...grouped].flatMap(([name, ids]) => ids.length === 1 ? [[name, ids[0]!] as const] : []));
}
function normalize(value: string): string { return value.trim().toLocaleLowerCase("zh-CN").replace(/\s+/g, ""); }
function matchStudy(entry: SolverHistoryEntry, studies: OptimizationStudy[]): OptimizationStudy | undefined {
  const byId = studies.find((study) => study.id === entry.studyId);
  if (byId) return byId;
  const normalizedName = normalize(entry.studyName ?? "");
  const byName = studies.filter((study) => normalize(study.name) === normalizedName);
  return byName.length === 1 ? byName[0] : undefined;
}
function asRecord(value: unknown): Record<string, unknown> { return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {}; }
function isActiveResultWheel(value: { rank: number; star: number }): boolean { return value.rank > 0 || value.star > 0; }
function isStudy(value: Record<string, unknown>): boolean { return Array.isArray(value.objectives) && Array.isArray(value.baseAttributes) && Array.isArray(value.inventory) && Boolean(value.solver); }
function unique(values: string[]): string[] { return [...new Set(values)]; }
function finite(value: unknown): number { const number = Number(value); return Number.isFinite(number) ? number : 0; }
function nonnegative(value: unknown): number { return Math.max(0, Math.floor(finite(value))); }
function clampInteger(value: unknown, minimum: number, maximum: number): number { return Math.max(minimum, Math.min(maximum, Math.round(finite(value)))); }
function validDate(value: unknown): string { const date = new Date(String(value ?? "")); return Number.isFinite(date.getTime()) ? date.toISOString() : new Date().toISOString(); }
